import { Link } from 'react-router-dom';
import { PiArrowSquareOutBold } from 'react-icons/pi';
import V2Modal from '@v2/components/ui/V2Modal';
import DealOwnerCell from '@v2/features/ploomes-deals/DealOwnerCell';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import type { SalesCalendarDay } from './types';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

function formatDateLabel(date: string) {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

type Props = {
    day: SalesCalendarDay | null;
    date: string | null;
    onClose: () => void;
};

export default function SalesDayDetailModal({ day, date, onClose }: Props) {
    const open = !!date;

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            size="2xl"
            title={date ? formatDateLabel(date) : 'Dia'}
            description={
                day
                    ? `${day.deals_count} venda(s) · ${brl(day.total_amount)} comercializado`
                    : 'Nenhuma venda registrada neste dia'
            }
        >
            {!day || day.deals.length === 0 ? (
                <p className="text-sm text-gray-500">
                    Não há deals com data de ganho (finish date) neste dia. As vendas aparecem pela data em que a proposta foi ganha no Ploomes.
                </p>
            ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            <tr>
                                <th className="px-4 py-2.5">Deal</th>
                                <th className="px-4 py-2.5">Cliente</th>
                                <th className="px-4 py-2.5">Vendedor</th>
                                <th className="px-4 py-2.5 text-right">Valor</th>
                                <th className="px-4 py-2.5" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {day.deals.map((deal) => (
                                <tr key={deal.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-gray-900 dark:text-gray-100">{deal.title}</p>
                                        <p className="text-xs text-gray-500">
                                            {[deal.internal_number, deal.deal_number].filter(Boolean).join(' · ') || `ID ${deal.id}`}
                                        </p>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                        {deal.client_name ?? '—'}
                                        {deal.segment && (
                                            <p className="text-xs text-gray-400">{deal.segment}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <DealOwnerCell
                                            name={deal.owner_name}
                                            avatarUrl={deal.owner_avatar_url}
                                            compactName
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                                        {brl(deal.amount)}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Link
                                            to={ploomesDealPath(deal)}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
                                        >
                                            Abrir
                                            <PiArrowSquareOutBold className="size-3.5" />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-50 font-semibold dark:bg-gray-800">
                            <tr>
                                <td colSpan={3} className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">Total do dia</td>
                                <td className="px-4 py-2.5 text-right text-gray-900 dark:text-gray-100">{brl(day.total_amount)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </V2Modal>
    );
}
